import { useState } from "react";

import { Icon } from "./Icon.js";
import { AddUser, UserKpis, UserList, useUsers } from "./UserList.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda AMMINISTRAZIONE, portata da `SettingsAdminTab.qml`.
 *
 * Due cose in una schermata: la **lingua** dell'interfaccia e gli **account**.
 * Stanno insieme perche' sono le due impostazioni che riguardano chi usa il
 * programma invece dell'impianto.
 *
 * La lingua e' una griglia di bandiere, non un menu a tendina: si sceglie una
 * volta sola e la si trova a colpo d'occhio. E' una preferenza della
 * POSTAZIONE, non dell'utente — davanti a un impianto il monitor e' sempre
 * quello e chi ci passa cambia — quindi resta anche cambiando persona.
 *
 * Da qui si arriva alla gestione utenti completa, che e' l'unico modo che
 * l'app desktop offre per aprirla.
 */
export function AdminTab({ onManageUsers }: { onManageUsers: () => void }) {
  const { t, lang, setLang, languages } = useI18n();
  const { can } = useSession();
  // Dieci secondi, come l'app desktop: due postazioni aperte sulla stessa
  // pagina devono vedere l'una le modifiche dell'altra.
  const { users, denied, error, setError, reload } = useUsers(10_000);
  const [localError, setLocalError] = useState("");

  const canChangeLanguage = can("changeLanguage");

  return (
    <div className="admin">
      <h3 className="admin__title">{t("settings.language").toUpperCase()}</h3>
      <div className="panel">
        <p className="muted small">{t("settings.languageDesc")}</p>
        <div className="langgrid">
          {languages.map((l) => (
            <button
              key={l.code}
              className={`langbtn${lang === l.code ? " langbtn--on" : ""}`}
              disabled={!canChangeLanguage}
              onClick={() => setLang(l.code)}
            >
              {/* Il codice della lingua in una pastiglia, non la bandiera:
                  Windows non disegna le emoji bandiera — al loro posto
                  compaiono le due lettere nude — e il `.qml` per questo mette
                  un badge con la sigla. Il browser fa la stessa sostituzione,
                  quindi senza la pastiglia restavano due lettere sospese. */}
              <span className="langbtn__code">{l.code.toUpperCase()}</span>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <UserKpis count={users.length} />

      <div className="admin__head">
        <h3 className="admin__title">{t("settings.userAccounts").toUpperCase()}</h3>
        <span className="emissions__spacer" />
        <button onClick={onManageUsers}>{t("users.userManagement")}</button>
        <button className="iconbtn" title={t("settings.userAccounts")} onClick={() => void reload()}>
          <Icon name="refresh-cw" size={14} />
        </button>
      </div>

      {denied && <p className="muted">{t("users.superAdminRequired")}</p>}
      {(error || localError) && <p className="alert">{error || localError}</p>}

      {!denied && (
        <>
          <UserList users={users} onChanged={() => void reload()} onError={setError} />
          <AddUser onCreated={() => void reload()} onError={setLocalError} />
        </>
      )}
    </div>
  );
}
